import { getHostAppVersion, getPreloadApi, hasElectronPreload } from '@renderer/lib/runtime'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import {
  currentRoute,
  readJsHeap,
  startRendererLifecycle,
  type LifecycleSnapshot,
  type RestartReport
} from './rendererLifecycle'

/**
 * Wires the renderer lifecycle tracker (./rendererLifecycle) to the real booth:
 * localStorage + sessionStorage, main's lifecycle IPC, the session store and the
 * BoothEvent queue. Electron only (see the module comment there).
 */

function storage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

function snapshot(): LifecycleSnapshot {
  const session = useSessionStateStore.getState()
  const settings = useSettingsStore.getState() as { deviceId?: string; defaultProjectId?: string }
  const inProgress = !!session.id && !session.timeline?.isCompleted
  return {
    deviceId: settings.deviceId ?? null,
    projectId: session.projectId || settings.defaultProjectId || null,
    sessionId: inProgress ? session.id : null,
    paid: inProgress && session.transaction?.status === 'settlement',
    route: currentRoute(),
    heapUsedMB: readJsHeap()?.usedMB ?? null
  }
}

function report(detail: RestartReport): void {
  console.warn(
    `[LIFECYCLE] Previous renderer ended without closing: ${detail.kind}` +
      (detail.reason ? ` (${detail.reason})` : '') +
      (detail.paid ? `, paid session ${detail.sessionId} lost` : '')
  )
  useBoothEventStore.getState().recordRendererRestart(detail)
}

export function installRendererLifecycle(): void {
  if (!hasElectronPreload()) return
  void startRendererLifecycle({
    local: storage('local'),
    session: storage('session'),
    now: () => Date.now(),
    getMainInfo: async () => {
      const api = getPreloadApi()
      // Shells older than the handler: the renderer infers from storage instead.
      if (typeof api?.getRendererLifecycle !== 'function') return null
      try {
        return await api.getRendererLifecycle()
      } catch {
        return null
      }
    },
    getAppVersion: getHostAppVersion,
    snapshot,
    report,
    onSessionChange: (listener) => {
      let last = ''
      return useSessionStateStore.subscribe((state) => {
        // The store changes on every photo; only a new session or a payment
        // settling changes what a crash would cost.
        const key = `${state.id}|${state.transaction?.status}|${state.timeline?.isCompleted}`
        if (key === last) return
        last = key
        listener()
      })
    },
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    addPageHideListener: (fn) => window.addEventListener('pagehide', fn)
  }).catch((err) => {
    console.warn('[LIFECYCLE] Tracker failed to start:', err)
  })
}
